import React, { useState, useCallback } from 'react';
import GalaxyMap from './GalaxyMap';
import QuestionModal from './QuestionModal';
import TestMode from './TestMode';
import { PLANET_DATA } from '../../data/planets';
import './SpaceQuest.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── Crystal counter pulse animation ─────────────────────────────────────────
function CrystalBadge({ count, flash }) {
  return (
    <div className={`sq-crystal-badge ${flash ? 'sq-crystal-flash' : ''}`}>
      <span className="sq-crystal-icon">💎</span>
      <span className="sq-crystal-count">{count}</span>
      <span className="sq-crystal-label">קריסטלים</span>
    </div>
  );
}

// ─── Game complete screen ─────────────────────────────────────────────────────
function GameCompleteOverlay({ crystals, onRestart }) {
  return (
    <div className="sq-game-complete-overlay">
      <div className="sq-game-complete-card">
        <span className="sq-gc-trophy">🏆</span>
        <h2 className="sq-gc-title">כל הכוכבים הושגו!</h2>
        <p className="sq-gc-crystals">אספת <strong>{crystals}</strong> קריסטלים</p>
        <button className="sq-gc-restart-btn" onClick={onRestart}>
          🚀 שחק שוב!
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function SpaceQuest() {
  // ── App mode ──
  const [appMode, setAppMode] = useState('adventure'); // 'adventure' | 'test'

  // ── Map state ──
  const [unlockedUpTo, setUnlockedUpTo] = useState(0);
  const [completedPlanets, setCompletedPlanets] = useState(new Set());
  const [planetProgress, setPlanetProgress] = useState({});
  const [newlyUnlocked, setNewlyUnlocked] = useState(null);

  // ── Session questions (original order, or shuffled on replay) ──
  const [sessionQuestions, setSessionQuestions] = useState({});

  // ── Crystal counter ──
  const [crystals, setCrystals] = useState(0);
  const [crystalFlash, setCrystalFlash] = useState(false);

  // ── Modal / question state ──
  const [modal, setModal] = useState(null);     // { planetId } | null
  const [currentQ, setCurrentQ] = useState(0);
  // feedback: null | 'correct' | 'wrong' | 'complete'
  const [feedback, setFeedback] = useState(null);

  // All planets finished → show end screen
  const allComplete = completedPlanets.size === PLANET_DATA.length && !modal;

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const flashCrystal = useCallback(() => {
    setCrystalFlash(true);
    setTimeout(() => setCrystalFlash(false), 700);
  }, []);

  const resetGame = useCallback(() => {
    setUnlockedUpTo(0);
    setCompletedPlanets(new Set());
    setPlanetProgress({});
    setNewlyUnlocked(null);
    setSessionQuestions({});
    setCrystals(0);
    setCrystalFlash(false);
    setModal(null);
    setCurrentQ(0);
    setFeedback(null);
  }, []);

  const openPlanet = useCallback(
    (planetId) => {
      if (planetId > unlockedUpTo) return;
      const planet = PLANET_DATA[planetId];
      const isReplay = completedPlanets.has(planetId);

      // Shuffle questions on replay; keep original order otherwise
      setSessionQuestions((prev) => ({
        ...prev,
        [planetId]: isReplay ? shuffleArray(planet.questions) : planet.questions,
      }));

      setModal({ planetId });
      // Replay always restarts from Q0; in-progress planet continues
      setCurrentQ(isReplay ? 0 : (planetProgress[planetId] || 0));
      setFeedback(null);
    },
    [unlockedUpTo, planetProgress, completedPlanets]
  );

  const closeModal = useCallback(() => {
    if (feedback === 'correct' || feedback === 'complete') return;
    setModal(null);
    setFeedback(null);
  }, [feedback]);

  // ── Answer handler ────────────────────────────────────────────────────────────
  const handleAnswer = useCallback(
    (optionIndex) => {
      if (feedback) return;

      const questions = sessionQuestions[modal.planetId] || PLANET_DATA[modal.planetId].questions;
      const question = questions[currentQ];
      const isCorrect = optionIndex === question.correct;

      if (!isCorrect) {
        setFeedback('wrong');
        setTimeout(() => setFeedback(null), 1400);
        return;
      }

      setCrystals((c) => c + 1);
      flashCrystal();

      const nextQ = currentQ + 1;
      const isLastQuestion = nextQ >= questions.length;

      if (isLastQuestion) {
        setFeedback('complete');

        setTimeout(() => {
          setCompletedPlanets((prev) => new Set([...prev, modal.planetId]));
          setPlanetProgress((prev) => ({ ...prev, [modal.planetId]: 0 }));

          // Only unlock + animate the next planet if it isn't already unlocked
          const nextPlanet = modal.planetId + 1;
          if (nextPlanet < PLANET_DATA.length && nextPlanet > unlockedUpTo) {
            setUnlockedUpTo(nextPlanet);
            setNewlyUnlocked(nextPlanet);
            setTimeout(() => setNewlyUnlocked(null), 2200);
          }

          setFeedback(null);
          setModal(null);
        }, 2000);
      } else {
        setFeedback('correct');
        setPlanetProgress((prev) => ({ ...prev, [modal.planetId]: nextQ }));

        setTimeout(() => {
          setFeedback(null);
          setCurrentQ(nextQ);
        }, 1100);
      }
    },
    [feedback, modal, currentQ, flashCrystal, sessionQuestions, unlockedUpTo]
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div
      className="sq-root"
      dir="rtl"
      style={{
        backgroundImage:
          "radial-gradient(ellipse at 50% 0%, rgba(13,27,62,0.55) 0%, rgba(3,7,18,0.88) 60%, rgba(0,0,0,0.95) 100%), url('/assets/space-bg.png')",
      }}
    >
      {/* ── Top bar ── */}
      <header className="sq-topbar">
        <h1 className="sq-title">🚀 מסע לכוכבים</h1>
        <div className="sq-topbar-actions">
          <button
            className={`sq-mode-toggle-btn${appMode === 'test' ? ' active' : ''}`}
            onClick={() => setAppMode(appMode === 'test' ? 'adventure' : 'test')}
          >
            {appMode === 'test' ? '🗺 הרפתקה' : '📋 מבחן'}
          </button>
          {appMode === 'adventure' && <CrystalBadge count={crystals} flash={crystalFlash} />}
        </div>
      </header>

      {appMode === 'test' ? (
        /* ── Test Mode ── */
        <main className="sq-main">
          <TestMode onExit={() => setAppMode('adventure')} />
        </main>
      ) : (
        <>
          {/* ── Galaxy map ── */}
          <main className="sq-main">
            <GalaxyMap
              unlockedUpTo={unlockedUpTo}
              completedPlanets={completedPlanets}
              planetProgress={planetProgress}
              newlyUnlocked={newlyUnlocked}
              onPlanetClick={openPlanet}
            />
          </main>

          {/* ── Question modal ── */}
          {modal && (
            <QuestionModal
              planet={PLANET_DATA[modal.planetId]}
              questions={sessionQuestions[modal.planetId] || PLANET_DATA[modal.planetId].questions}
              questionIndex={currentQ}
              feedback={feedback}
              onAnswer={handleAnswer}
              onClose={closeModal}
            />
          )}

          {/* ── Game complete overlay ── */}
          {allComplete && (
            <GameCompleteOverlay crystals={crystals} onRestart={resetGame} />
          )}
        </>
      )}
    </div>
  );
}
